import { definedMap } from "@glideapps/ts-necessities";
import md5 from "blueimp-md5";
import type { BasePrimitiveValue } from "../../common/components/computation-types";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import type { TableName } from "../../common/database-strings";
import type {
    ColumnType,
    Formula,
    PrimitiveGlideTypeKind,
    SpecialValueKind,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import {
    BinaryPredicateFormulaOperator,
    getTableColumn,
    getTableName,
    isRelationType,
    makeArrayType,
    makeTableRef,
    UnaryPredicateFormulaOperator,
} from "../../common/description";
import { getFeatureSetting } from "../../common/feature-settings";
import { BinaryMathFunction, ReduceOperator, ReduceToMemberByOperator, UnaryMathFunction } from "../../common/formula";
import { assert, assertNever } from "../../common/Support";
import type { FetchingBuilder, UserProfileRowBuild } from "../components";
import type { LetBinding, SerializedElementBuilder } from "../serialized-component";
import { compileFormulaToJavaScript } from "./compile-to-js";
import type {
    BoundContext,
    CodeGenerator,
    CompileResult,
    InitialFormulaBinding,
    InternalCompileResult,
} from "./compiler";
import {
    canGlobalizeFormula,
    compileFormulaWithCodeGenerator,
    getEffectiveDisplayFormulaForColumn,
    isNumberFormulaTypeKind,
    isPrimitiveFormulaType,
} from "./compiler";
import {
    containingScreenContextName,
    containingScreenVariableName,
    decomposeFilterReferenceFormula,
    displayFormulaInputVariableName,
} from "./formula-specifications";

class SerializedComponentCodeGenerator implements CodeGenerator<SerializedComponent, false> {
    public readonly inlinedColumnFormats = false;
    public readonly mappedRowProps = false;

    private readonly _issues: string[] = [];

    constructor(private readonly _builder: FetchingBuilder) {}

    public get issues(): readonly string[] {
        return this._issues;
    }

    public getPropsFromResult(): false {
        return false;
    }

    public makeVariableName(n: string): string {
        return `var-${n}`;
    }

    public makeContextVariableName(contextName: string | undefined): string {
        if (contextName === undefined) {
            return "outer-context";
        } else {
            return `context-${contextName}`;
        }
    }

    public constant(value: BasePrimitiveValue | null): SerializedComponent {
        return this._builder.b.constant(value);
    }

    public getColumn(
        column: TableColumn,
        table: TableGlideType,
        row: SerializedComponent,
        forDataEditor: boolean
    ): SerializedComponent {
        let value = this._builder.b.getProperty(column.name, row, table);
        if (isRelationType(column.type)) {
            value = this._builder.fetchLinkColumn(column, value, forDataEditor);
        }
        return value;
    }

    public inContext(variable: string, body: InternalCompileResult<SerializedComponent>): SerializedComponent {
        const { b } = this._builder;
        return b.let([[variable, b.getContext]], body.value);
    }

    public getContextVariable(name: string, isInner: boolean): SerializedComponent {
        const { b } = this._builder;
        if (isInner) {
            return b.getContext;
        } else {
            return b.getVariable(name);
        }
    }

    public orderComparison(
        builtin: string,
        left: InternalCompileResult<SerializedComponent>,
        right: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        const { b } = this._builder;
        let leftValue: SerializedComponent;
        let rightValue: SerializedComponent;
        const leftIsNumber = isNumberFormulaTypeKind(left.type?.kind);
        const rightIsNumber = isNumberFormulaTypeKind(right.type?.kind);

        if (leftIsNumber && left.typeIsPrecise && (!rightIsNumber || !right.typeIsPrecise)) {
            leftValue = left.value;
            rightValue = b.convertToNumber(right.value);
        } else if (rightIsNumber && right.typeIsPrecise && (!leftIsNumber || !left.typeIsPrecise)) {
            leftValue = b.convertToNumber(left.value);
            rightValue = right.value;
        } else {
            leftValue = left.value;
            rightValue = right.value;
        }
        return b.builtinCall(builtin, [leftValue, rightValue]);
    }

    public withDefaultFalse(value: InternalCompileResult<SerializedComponent>): SerializedComponent {
        const { b } = this._builder;
        return b.withDefault(value.value, b.constant(false));
    }

    public fetchTableRows(table: TableName, forDataEditor: boolean): SerializedComponent {
        return this._builder.fetchTableRows(table, forDataEditor);
    }

    public filterRows(
        rows: InternalCompileResult<SerializedComponent>,
        predicate: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.filter(rows.value, predicate.value);
    }

    public mapRows(
        rows: InternalCompileResult<SerializedComponent>,
        fn: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        const { b } = this._builder;
        return b.filter(b.map(rows.value, fn.value), b.isDefined(b.getContext));
    }

    public reduce(
        op: ReduceOperator,
        rows: InternalCompileResult<SerializedComponent>,
        value: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        const { b } = this._builder;
        switch (op) {
            case ReduceOperator.AllTrue:
                return b.primitive("AllTrue", { mapper: value.value, list: rows.value });
            case ReduceOperator.SomeTrue:
                return b.primitive("SomeTrue", { mapper: value.value, list: rows.value });
            case ReduceOperator.Sum:
                return b.primitive("Sum", { number: value.value, list: rows.value });
            case ReduceOperator.CountNonEmpty:
                return b.primitive("Count", {
                    mapper: b.builtinCall("IsNotEmpty", [value.value]),
                    list: rows.value,
                });
            case ReduceOperator.CountUnique:
                return b.listLength(b.uniqueAndMapValues(rows.value, value.value));
            case ReduceOperator.CountTrue:
                return b.primitive("Count", {
                    mapper: b.areEqual(value.value, b.constant(true)),
                    list: rows.value,
                });
            case ReduceOperator.CountNotTrue:
                return b.primitive("Count", {
                    mapper: b.not(b.areEqual(value.value, b.constant(true))),
                    list: rows.value,
                });
            case ReduceOperator.Average:
                return b.builtinCall("Divide", [
                    b.primitive("Sum", { number: value.value, list: rows.value }),
                    b.primitive("Count", {
                        mapper: b.builtinCall("IsNotEmpty", [value.value]),
                        list: rows.value,
                    }),
                ]);
            case ReduceOperator.Range:
                return b.builtinCall("Subtract", [
                    b.primitive("Maximum", {
                        mapper: value.value,
                        list: rows.value,
                        result: value.value,
                    }),
                    b.primitive("Minimum", {
                        mapper: value.value,
                        list: rows.value,
                        result: value.value,
                    }),
                ]);
            default:
                return assertNever(op);
        }
    }

    public reduceToMemberBy(
        op: ReduceToMemberByOperator,
        list: InternalCompileResult<SerializedComponent>,
        mapper: InternalCompileResult<SerializedComponent>,
        result: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        const { b } = this._builder;
        switch (op) {
            case ReduceToMemberByOperator.Minimum:
                return b.primitive("Minimum", {
                    mapper: mapper.value,
                    list: list.value,
                    result: result.value,
                });
            case ReduceToMemberByOperator.Maximum:
                return b.primitive("Maximum", {
                    mapper: mapper.value,
                    list: list.value,
                    result: result.value,
                });
            default:
                return assertNever(op);
        }
    }

    public joinStrings(
        list: InternalCompileResult<SerializedComponent>,
        mapper: InternalCompileResult<SerializedComponent>,
        separator: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.joinStringsWithSeparator(list.value, mapper.value, separator.value);
    }

    public splitString(
        string: InternalCompileResult<SerializedComponent>,
        separator: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.splitString(string.value, separator.value);
    }

    public findRow(
        list: InternalCompileResult<SerializedComponent>,
        predicate: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.find(list.value, predicate.value);
    }

    public ifThenElse(
        condition: InternalCompileResult<SerializedComponent>,
        consequent: InternalCompileResult<SerializedComponent>,
        alternative: InternalCompileResult<SerializedComponent> | undefined
    ): SerializedComponent {
        return this._builder.b.if(condition.value, consequent.value, alternative?.value);
    }

    public checkValue(
        op: UnaryPredicateFormulaOperator,
        value: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        if (op === UnaryPredicateFormulaOperator.IsNotEmpty) {
            return this._builder.b.isNotEmpty(value.value);
        } else {
            return assertNever(op);
        }
    }

    public compareValues(
        op: BinaryPredicateFormulaOperator,
        left: InternalCompileResult<SerializedComponent>,
        right: InternalCompileResult<SerializedComponent>
    ): SerializedComponent | undefined {
        const { b } = this._builder;
        switch (op) {
            case BinaryPredicateFormulaOperator.Equals:
                return b.areEqual(left.value, right.value);
            case BinaryPredicateFormulaOperator.ContainsString:
            case BinaryPredicateFormulaOperator.IsContainedInString:
                if (!isPrimitiveFormulaType(left.type) || !isPrimitiveFormulaType(right.type)) {
                    this._issues.push("Argument to ContainsString is not a string");
                    return undefined;
                }
                if (op === BinaryPredicateFormulaOperator.ContainsString) {
                    return b.builtinCall("ContainsString", [left.value, right.value]);
                } else {
                    return b.builtinCall("ContainsString", [right.value, left.value]);
                }
            case BinaryPredicateFormulaOperator.IsLessThan:
                return this.orderComparison("IsLessThan", left, right);
            case BinaryPredicateFormulaOperator.IsGreaterThan:
                return this.orderComparison("IsGreaterThan", left, right);
            case BinaryPredicateFormulaOperator.IsGreaterOrEqualTo:
                return this.orderComparison("IsGreaterOrEqualTo", left, right);
            case BinaryPredicateFormulaOperator.IsLessOrEqualTo:
                return this.orderComparison("IsLessOrEqualTo", left, right);
            case BinaryPredicateFormulaOperator.MatchesEmailAddress:
                if (right.type?.kind === "array") {
                    return b.primitive("Some", {
                        list: right.value,
                        predicate: b.areEmailsEqual(left.value, b.getContext),
                    });
                } else {
                    return b.areEmailsEqual(left.value, right.value);
                }
            default:
                return assertNever(op);
        }
    }

    public getUserProfileRow(userProfile: UserProfileRowBuild): SerializedComponent {
        return userProfile.row;
    }

    public applyColumnFormat(
        value: InternalCompileResult<SerializedComponent>,
        table: TableGlideType,
        column: TableColumn
    ): SerializedComponent {
        return withColumnFormat(this._builder, value.value, table, column);
    }

    public textTemplate(
        template: InternalCompileResult<SerializedComponent>,
        replacements: readonly SerializedComponent[]
    ): SerializedComponent {
        return this._builder.b.replaceStrings(template.value, replacements);
    }

    public arrayContains(
        array: InternalCompileResult<SerializedComponent>,
        item: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.arrayContains(array.value, item.value);
    }

    public arraysOverlap(
        left: InternalCompileResult<SerializedComponent>,
        right: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.arraysOverlap(left.value, right.value);
    }

    public and(
        left: InternalCompileResult<SerializedComponent>,
        right: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.and(left.value, right.value);
    }

    public or(
        left: InternalCompileResult<SerializedComponent>,
        right: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.or(left.value, right.value);
    }

    public not(value: InternalCompileResult<SerializedComponent>): SerializedComponent {
        const { b } = this._builder;
        // FIXME: Why do we convert to boolean here?  Not already does that.
        return b.not(b.convertToBoolean(value.value));
    }

    public specialValue(kind: SpecialValueKind): SerializedComponent {
        return this._builder.b.specialValue(kind);
    }

    public convertToType(
        value: InternalCompileResult<SerializedComponent>,
        typeKind: PrimitiveGlideTypeKind
    ): SerializedComponent | undefined {
        const { b } = this._builder;
        switch (typeKind) {
            case "number":
                return b.convertToNumber(value.value);
            case "date-time":
                return b.parseUserDateTime(value.value);
            default:
                this._issues.push(`Cannot convert to type ${typeKind}`);
                return undefined;
        }
    }

    public startOfDay(dateTime: InternalCompileResult<SerializedComponent>): SerializedComponent {
        return this._builder.b.startOfDay(dateTime.value);
    }

    public endOfDay(dateTime: InternalCompileResult<SerializedComponent>): SerializedComponent {
        return this._builder.b.builtinCall("EndOfDay", [dateTime.value]);
    }

    public with(
        value: InternalCompileResult<SerializedComponent>,
        context: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.bindContextToValue(value.value, context.value);
    }

    public getNth(
        list: InternalCompileResult<SerializedComponent>,
        index: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.listNth(list.value, index.value);
    }

    public getNthLast(
        list: InternalCompileResult<SerializedComponent>,
        index: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.listNthLast(list.value, index.value);
    }

    public randomPick(list: InternalCompileResult<SerializedComponent>, uniqueID: string): SerializedComponent {
        const { b } = this._builder;
        return b.builtinCall("RandomPick", [list.value, b.constant(uniqueID)]);
    }

    public isInRange(
        value: InternalCompileResult<SerializedComponent>,
        start: InternalCompileResult<SerializedComponent>,
        end: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        const { b } = this._builder;
        return b.let(
            [["v", value.value]],
            b.and(b.isGreaterOrEqualTo(b.getVariable("v"), start.value), b.isLessThan(b.getVariable("v"), end.value))
        );
    }

    public assignVariables(
        lets: readonly [string, InternalCompileResult<SerializedComponent>][],
        body: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.let(
            lets.map(([n, r]) => [n, r.value]),
            body.value
        );
    }

    public getVariable(name: string): SerializedComponent {
        return this._builder.b.getVariable(name);
    }

    public randomMath(): SerializedComponent {
        return this._builder.b.builtinCall("Random", []);
    }

    public unaryMath(op: UnaryMathFunction, operand: InternalCompileResult<SerializedComponent>): SerializedComponent {
        const { b } = this._builder;

        let builtinName: string;
        let property: string | undefined;
        let convertToDateTime = false;
        switch (op) {
            case UnaryMathFunction.Negate:
                builtinName = "Negate";
                break;
            case UnaryMathFunction.Absolute:
                builtinName = "Absolute";
                break;
            case UnaryMathFunction.Floor:
                builtinName = "Floor";
                break;
            case UnaryMathFunction.Ceiling:
                builtinName = "Ceiling";
                break;
            case UnaryMathFunction.Round:
                builtinName = "Round";
                break;
            case UnaryMathFunction.Sign:
                builtinName = "Sign";
                break;
            case UnaryMathFunction.Truncate:
                builtinName = "Truncate";
                break;
            case UnaryMathFunction.Sine:
                builtinName = "Sine";
                break;
            case UnaryMathFunction.Cosine:
                builtinName = "Cosine";
                break;
            case UnaryMathFunction.Tangent:
                builtinName = "Tangent";
                break;
            case UnaryMathFunction.ArcSine:
                builtinName = "ArcSine";
                break;
            case UnaryMathFunction.ArcCosine:
                builtinName = "ArcCosine";
                break;
            case UnaryMathFunction.ArcTangent:
                builtinName = "ArcTangent";
                break;
            case UnaryMathFunction.Logarithm:
                builtinName = "Logarithm";
                break;
            case UnaryMathFunction.Year:
                builtinName = "DecomposeDateTime";
                property = "year";
                convertToDateTime = true;
                break;
            case UnaryMathFunction.Month:
                builtinName = "DecomposeDateTime";
                property = "month";
                convertToDateTime = true;
                break;
            case UnaryMathFunction.Day:
                builtinName = "DecomposeDateTime";
                property = "day";
                convertToDateTime = true;
                break;
            case UnaryMathFunction.Hour:
                builtinName = "DecomposeDateTime";
                property = "hour";
                convertToDateTime = true;
                break;
            case UnaryMathFunction.Minute:
                builtinName = "DecomposeDateTime";
                property = "minute";
                convertToDateTime = true;
                break;
            case UnaryMathFunction.Second:
                builtinName = "DecomposeDateTime";
                property = "second";
                convertToDateTime = true;
                break;
            case UnaryMathFunction.Weekday:
                builtinName = "DecomposeDateTime";
                property = "weekday";
                convertToDateTime = true;
                break;
            case UnaryMathFunction.WeekNumber:
                builtinName = "DecomposeDateTime";
                property = "weekNumber";
                convertToDateTime = true;
                break;
            default:
                return assertNever(op);
        }
        let input = operand.value;
        if (convertToDateTime) {
            input = b.parseUserDateTime(input);
        }
        let result = b.builtinCall(builtinName, [input]);
        if (property !== undefined) {
            result = b.getNonTableProperty(property, result);
        }
        return result;
    }

    public binaryMath(
        op: BinaryMathFunction,
        left: InternalCompileResult<SerializedComponent>,
        leftIsConstant: boolean,
        right: InternalCompileResult<SerializedComponent>,
        rightIsConstant: boolean
    ): SerializedComponent {
        let builtinName: string;
        switch (op) {
            case BinaryMathFunction.Add:
                if (leftIsConstant || rightIsConstant) {
                    builtinName = "AddToConstant";
                } else {
                    builtinName = "Add";
                }
                break;
            case BinaryMathFunction.Subtract:
                if (leftIsConstant) {
                    builtinName = "SubtractFromConstant";
                } else {
                    builtinName = "Subtract";
                }
                break;
            case BinaryMathFunction.Multiply:
                builtinName = "Multiply";
                break;
            case BinaryMathFunction.Divide:
                builtinName = "Divide";
                break;
            case BinaryMathFunction.Modulo:
                builtinName = "Modulo";
                break;
            case BinaryMathFunction.Minimum:
                builtinName = "Minimum2";
                break;
            case BinaryMathFunction.Maximum:
                builtinName = "Maximum2";
                break;
            case BinaryMathFunction.Power:
                builtinName = "Power";
                break;
            case BinaryMathFunction.Logarithm:
                builtinName = "Logarithm";
                break;
            case BinaryMathFunction.ArcTangent2:
                builtinName = "ArcTangent2";
                break;
            case BinaryMathFunction.Round:
                builtinName = "Round";
                break;
            case BinaryMathFunction.Truncate:
                builtinName = "Truncate";
                break;
            default:
                return assertNever(op);
        }
        return this._builder.b.builtinCall(builtinName, [left.value, right.value]);
    }

    public subtractDates(
        left: InternalCompileResult<SerializedComponent>,
        right: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        const { b } = this._builder;
        return b.builtinCall("SubtractDates", [b.parseUserDateTime(left.value), b.parseUserDateTime(right.value)]);
    }

    public addDurationToDate(
        date: InternalCompileResult<SerializedComponent>,
        duration: InternalCompileResult<SerializedComponent>,
        negate: boolean
    ): SerializedComponent {
        const { b } = this._builder;
        let right = duration.value;
        if (negate) {
            right = b.builtinCall("Negate", [right]);
        }
        return b.builtinCall("AddDurationToDate", [b.parseUserDateTime(date.value), right]);
    }

    public formatNumberFixed(
        value: InternalCompileResult<SerializedComponent>,
        decimalsAfterPoint: InternalCompileResult<SerializedComponent>,
        groupSeparator: InternalCompileResult<SerializedComponent>,
        currency: InternalCompileResult<SerializedComponent> | undefined
    ): SerializedComponent {
        const args = [value.value, decimalsAfterPoint.value, groupSeparator.value];
        if (currency !== undefined) {
            args.push(currency.value);
        }
        return this._builder.b.builtinCall("FormatNumberFixed", args);
    }

    public formatDateTime(
        value: InternalCompileResult<SerializedComponent>,
        dateFormat: InternalCompileResult<SerializedComponent> | undefined,
        timeFormat: InternalCompileResult<SerializedComponent> | undefined
    ): SerializedComponent {
        const { b } = this._builder;
        const args = [
            b.parseUserDateTime(value.value),
            dateFormat?.value ?? b.constant(null),
            timeFormat?.value ?? b.constant(null),
        ];
        return b.builtinCall("FormatDateTime", args);
    }

    public currentLocation(): SerializedComponent {
        return this._builder.b.currentLocation();
    }

    public geocodeAddress(value: InternalCompileResult<SerializedComponent>, quotaKey: string): SerializedComponent {
        return this._builder.b.lookupGeoAddress(value.value, quotaKey);
    }

    public geoDistance(
        left: InternalCompileResult<SerializedComponent>,
        right: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.builtinCall("GeoDistance", [left.value, right.value]);
    }

    public generateImage(
        input: InternalCompileResult<SerializedComponent>,
        imageKind: InternalCompileResult<SerializedComponent>
    ): SerializedComponent {
        return this._builder.b.builtinCall("GenerateImage", [input.value, imageKind.value]);
    }

    public userAPIFetch(webhookID: string, params: readonly SerializedComponent[]): SerializedComponent {
        return this._builder.b.builtinCall("UserAPIFetch", [
            this._builder.b.constant(webhookID),
            this._builder.b.list(params),
        ]);
    }

    public constructURL(
        scheme: InternalCompileResult<SerializedComponent>,
        host: InternalCompileResult<SerializedComponent>,
        path: InternalCompileResult<SerializedComponent>,
        params: readonly SerializedComponent[]
    ): SerializedComponent {
        return this._builder.b.builtinCall("ConstructURL", [
            scheme.value,
            host.value,
            path.value,
            this._builder.b.list(params),
        ]);
    }

    public yesCodeFetch(
        url: InternalCompileResult<SerializedComponent>,
        params: readonly [InternalCompileResult<SerializedComponent>, InternalCompileResult<SerializedComponent>][]
    ): SerializedComponent {
        const { b } = this._builder;

        const paramsArray = params.map(([k, v]) => b.list([k.value, v.value]));
        return b.builtinCall("YesCodeFetch", [url.value, b.list(paramsArray)]);
    }
}

function convertForFilterReference(
    b: SerializedElementBuilder,
    value: SerializedComponent,
    type: ColumnType
): SerializedComponent {
    // Here's where the old computation model puts in the call for
    // ##convertingRelationKeys.
    if (type.kind === "array") {
        return b.map(value, b.convertToLocaleAgnosticString(b.getContext));
    } else {
        return b.convertToLocaleAgnosticString(value);
    }
}

function compileSpecialCase(
    builder: FetchingBuilder,
    formula: Formula,
    hostTable: TableGlideType,
    forDataEditor: boolean
): CompileResult<SerializedComponent> | undefined {
    const filter = decomposeFilterReferenceFormula(formula);
    if (filter === undefined) return undefined;

    const {
        multiple,
        targetTable: targetUniversalTableName,
        targetColumn: targetColumnName,
        hostColumn: hostColumnName,
    } = filter;

    const hostColumn = getTableColumn(hostTable, hostColumnName);
    if (hostColumn === undefined) return undefined;

    const targetTable = builder.findTable(targetUniversalTableName);
    if (targetTable === undefined) return undefined;
    const targetTableName = getTableName(targetTable);

    const targetColumn = getTableColumn(targetTable, targetColumnName);
    if (targetColumn === undefined) return undefined;

    const { b } = builder;

    let value = b.primitive("FilterWhereIdentical", {
        indexName: b.constant(
            `ident:${md5(targetTableName.name)}:${md5(targetColumnName)}:${forDataEditor.toString()}`
        ),
        rows: builder.fetchTableRows(targetTableName, forDataEditor),
        keyForRow: convertForFilterReference(
            b,
            b.getProperty(targetColumnName, b.getContext, targetTable),
            targetColumn.type
        ),
        needle: convertForFilterReference(b, b.getProperty(hostColumnName, b.getContext, hostTable), hostColumn.type),
        hostIsArrayType: b.constant(hostColumn.type.kind === "array"),
        targetIsArrayType: b.constant(targetColumn.type.kind === "array"),
    });
    let type: ColumnType = makeTableRef(targetTable);

    if (multiple) {
        type = makeArrayType(type);
    } else {
        value = b.listNth(value, 0);
    }

    return { success: true, value, type, typeIsPrecise: true, geocodeQuotaKeys: new Set() };
}

export function compileFormula(
    builder: FetchingBuilder,
    outerTable: TableGlideType | undefined,
    formula: Formula,
    allowGlobal: boolean,
    forDataEditor: boolean,
    uniqueID: string,
    initialBindings?: readonly InitialFormulaBinding<SerializedComponent>[],
    boundContexts?: readonly BoundContext<false>[]
): CompileResult<SerializedComponent> {
    // logInfo("compiling formula", formula, initialBindings, boundContexts);

    if (outerTable !== undefined) {
        const specialCaseResult = compileSpecialCase(builder, formula, outerTable, forDataEditor);
        if (specialCaseResult !== undefined) {
            return specialCaseResult;
        }
    }

    const codegen = new SerializedComponentCodeGenerator(builder);
    let finalResult = compileFormulaWithCodeGenerator(
        codegen,
        builder,
        outerTable,
        false,
        formula,
        forDataEditor,
        uniqueID,
        initialBindings,
        boundContexts
    );
    if (!finalResult.success) return finalResult;

    const formulaMD5 = md5(JSON.stringify(formula) + forDataEditor.toString());

    const compiledPrimitiveName = `Formula${formulaMD5}`;

    if (getFeatureSetting("compileFormulaToJS")) {
        let jsCompiled = builder.getCompiledJSCode(compiledPrimitiveName);
        if (jsCompiled !== false) {
            if (jsCompiled === undefined) {
                jsCompiled = compileFormulaToJavaScript(
                    builder,
                    outerTable,
                    formula,
                    forDataEditor,
                    uniqueID,
                    compiledPrimitiveName
                );
                if (jsCompiled !== undefined) {
                    finalResult = { ...finalResult, value: jsCompiled };
                } else {
                    builder.setCompiledJSFailed?.(compiledPrimitiveName);
                }
            } else {
                finalResult = { ...finalResult, value: jsCompiled };
            }
        }
    }

    if (allowGlobal && canGlobalizeFormula(formula, [], [])) {
        finalResult = { ...finalResult, value: builder.b.global(formulaMD5, finalResult.value) };
    }

    return finalResult;
}

export function compileFilter(
    builder: FetchingBuilder,
    table: TableGlideType,
    predicate: Formula,
    containingScreenTable: TableGlideType | undefined,
    containingScreenContext: SerializedComponent | undefined
): CompileResult<SerializedComponent> {
    let boundContexts: BoundContext<false>[] | undefined;
    let lets: LetBinding[] | undefined;
    if (containingScreenContext !== undefined) {
        assert(containingScreenTable !== undefined);
        boundContexts = [
            [containingScreenContextName, getTableName(containingScreenTable), containingScreenVariableName, false],
        ];
        lets = [[containingScreenVariableName, containingScreenContext]];
    }

    const result = compileFormula(
        builder,
        table,
        predicate,
        false,
        false,
        "compileFilter shouldn't need unique IDs",
        undefined,
        boundContexts
    );
    if (lets === undefined || !result.success) return result;

    return {
        ...result,
        value: builder.b.let(lets, result.value),
    };
}

export function compileDisplayFormula(
    builder: FetchingBuilder,
    value: SerializedComponent,
    table: TableGlideType,
    column: TableColumn | undefined
): SerializedComponent | undefined {
    if (column === undefined) return undefined;

    if (builder.isBuilder) {
        const { b } = builder;
        return b.builtinCall("WithColumnFormatInBuilder", [
            b.constant("default"),
            b.constant(getTableName(table).name),
            b.constant(column.name),
            value,
        ]);
    } else {
        const displayFormula = getEffectiveDisplayFormulaForColumn(builder, table, column);
        if (displayFormula === undefined) return undefined;

        const result = compileFormula(
            builder,
            undefined,
            displayFormula,
            false,
            false,
            "compileDisplayFormula shouldn't need unique IDs",
            [[displayFormulaInputVariableName, { value, type: column.type, typeIsPrecise: false }]]
        );
        if (result.success) {
            return result.value;
        } else {
            builder.error(`Could not compile display formula ${result.issues.join(", ")}`);
            return value;
        }
    }
}

export function withColumnFormat(
    builder: FetchingBuilder,
    value: SerializedComponent,
    table: TableGlideType,
    column: TableColumn | undefined
): SerializedComponent {
    return compileDisplayFormula(builder, value, table, column) ?? value;
}

export function getPropertyWithColumnFormat(
    builder: FetchingBuilder,
    table: TableGlideType,
    columnName: string,
    context?: SerializedComponent
): SerializedComponent {
    const { b } = builder;
    const value = b.getProperty(columnName, context ?? b.getContext, table);

    const column = definedMap(table, t => getTableColumn(t, columnName));
    return withColumnFormat(builder, value, table, column);
}
